// src/pages/gt3x/Rules.jsx
// RaceVision Regelwerk V2 – 15 Kapitel aus EINEM Google Doc / DB-Cache.
import { useEffect, useMemo, useRef, useState } from "react";
import DOMPurify from "dompurify";
import PageHero from "../../components/PageHero.jsx";
import { fetchPublicRulebook } from "../../lib/ligamanagerApi";
import "./RulebookV2.css";

const CHAPTERS = [
    { no: 1, title: "Seriesoverview", icon: "🏁", description: "Serie & Ligaformat", tone: "lime" },
    { no: 2, title: "Anmeldung & Orga", icon: "📋", description: "Teilnahme & Organisation", tone: "blue" },
    { no: 3, title: "Fahrzeuge", icon: "🏎️", description: "Fahrzeugwahl & Design", tone: "green" },
    { no: 4, title: "Qualy", icon: "⏱️", description: "Qualifying-Regeln", tone: "amber" },
    { no: 5, title: "WarmUp", icon: "🔧", description: "Vorbereitung", tone: "lime" },
    { no: 6, title: "Rennen", icon: "🏆", description: "Ablauf & Rennregeln", tone: "blue" },
    { no: 7, title: "Tracklimits", icon: "🚧", description: "Streckenbegrenzungen", tone: "green" },
    { no: 8, title: "Flaggen", icon: "🚩", description: "Flaggensignale", tone: "amber" },
    { no: 9, title: "Verhalten auf der Strecke", icon: "🛣️", description: "Fahrverhalten & Zweikämpfe", tone: "lime" },
    { no: 10, title: "Bewertung von Strafen", icon: "⚖️", description: "Beurteilung von Vorfällen", tone: "blue" },
    { no: 11, title: "Strafpunktesystem", icon: "📊", description: "Punkte & Konsequenzen", tone: "green" },
    { no: 12, title: "Proteste", icon: "📨", description: "Verfahren & Fristen", tone: "amber" },
    { no: 13, title: "Verhaltensregeln", icon: "🤝", description: "Kommunikation & Fair Play", tone: "lime" },
    { no: 14, title: "Regelungssystematik", icon: "📖", description: "Grundlagen & Auslegung", tone: "blue" },
    { no: 15, title: "Anlagen", icon: "📑", description: "Wertung & Strafenkatalog", tone: "green" },
];

const VERSION_HEADING = /^(?:änderungsverfolgung|versionsänderungen|änderungshistorie|versionshistorie|changelog)\s*:?\s*$/i;
const CHAPTER_MARKER = /^\[\[chapter:(\d{1,2})\]\]$/i;
// Verhindert, dass normale Sätze wie "10 Minuten" oder "5 Punkte" als Kapitel gelten.
const CHAPTER_TITLE_HINTS = {
    1: /series|overview|liga|gt3\s*xperience/i,
    2: /anmeldung|einschreibung|organisation|orga/i,
    3: /fahrzeug|fahrzeugwahl|fahrzeugdesign/i,
    4: /qualy|qualifying|qualifikation/i,
    5: /warm.?up/i,
    6: /rennen|race/i,
    7: /track.?limit|streckenlimit/i,
    8: /flaggen|flaggensignale/i,
    9: /verhalten.*strecke|zweikampf/i,
    10: /bewertung|strafe|strafpunkt/i,
    11: /strafpunkt|proteste/i,
    12: /proteste|verhalten|kommunikation/i,
    13: /verhalten|regelungssystematik/i,
    14: /regelungssystematik|anlage.*wertung/i,
    15: /anlage|strafenkatalog|anhang/i,
};

const NUMERIC_SUBPOINT = /^((?:\d+\.)+\d+)\.?(?=\s|$)/;
const DOC_PILL = /\[\[pill:(red|yellow|orange|blue|green|neutral):([^\]]+)\]\]/gi;
const DOC_ROW = /\[\[row:(red|yellow|orange|blue|green|neutral|penalty)\]\]/i;

function prettyDate(value) {
    if (!value) return "—";
    let raw = String(value).trim();
    if (!/(?:Z|[+-]\d\d:?\d\d)$/i.test(raw) && /^\d{4}-\d\d-\d\d[T\s]\d\d:\d\d/.test(raw)) {
        // MySQL/FastAPI liefert UTC im Projekt bislang ohne Offset.
        raw = `${raw.replace(" ", "T")}Z`;
    }
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString("de-DE", {
        timeZone: "Europe/Berlin", day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
    });
}

function textOf(node) {
    return String(node?.textContent || "").replace(/\s+/g, " ").trim();
}

function hasInternalLink(node) {
    return Boolean(node.querySelector('a[href^="#"], a[href*="#heading="]'));
}

function isSimpleBlock(node) {
    return !node.querySelector("p, div, section, article, table, ul, ol, h1, h2, h3, h4");
}

function chapterNumberFromNode(node) {
    const marker = textOf(node).match(CHAPTER_MARKER);
    if (marker) return Number(marker[1]);
    if (!isSimpleBlock(node) || hasInternalLink(node)) return null;
    const txt = textOf(node);
    // Ein Kapitel beginnt mit einer eigenen Zeile: "3. Fahrzeugwahl" oder "3 Fahrzeugwahl".
    // Unterpunkte wie 3.1 dürfen KEIN neues Kapitel auslösen.
    const match = txt.match(/^(\d{1,2})\.?\s+(.+)$/);
    if (!match || txt.length > 160) return null;
    const no = Number(match[1]);
    if (no < 1 || no > 15) return null;
    const looksLikeHeading = /^(H1|H2|H3|H4)$/.test(node.tagName);
    return looksLikeHeading || CHAPTER_TITLE_HINTS[no].test(match[2]) ? no : null;
}

function stripChapterMarker(node) {
    return CHAPTER_MARKER.test(textOf(node));
}

function unwrapGoogleBody(root) {
    // Google Docs exportiert oft einen einzigen äußeren <div>-Container.
    for (let i = 0; i < 3 && root.children.length === 1; i += 1) {
        const child = root.firstElementChild;
        if (child?.tagName !== "DIV" || child.children.length < 2) break;
        while (child.firstChild) root.insertBefore(child.firstChild, child);
        child.remove();
    }
}

/**
 * Mehrzeilige Regelpunkte als Nummernspalte + Textspalte aufbauen.
 * Range.extractContents() erhaelt vorhandene Google-Docs-Formatierung,
 * Links und inline-Pills, selbst wenn die Nummer in einem <span> steht.
 */
function normalizeRuleLineLayout(node) {
    if (!node?.classList?.contains("rv-rule")) return;
    if (node.classList.contains("rv-rule--hanging")) return;

    // Tabellenzellen behalten ihre native Tabellenstruktur.
    if (node.matches("td, th")) return;

    const number = node.querySelector(".rv-num");
    if (!number) return;

    // Den kompletten Inhalt NACH der Nummer inklusive HTML-Auszeichnungen erhalten.
    const afterRange = document.createRange();
    afterRange.setStartAfter(number);
    afterRange.setEnd(node, node.childNodes.length);
    const afterContent = afterRange.extractContents();

    // Fuehrende Leerzeichen / sonstigen Inhalt VOR der Nummer erhalten.
    const beforeRange = document.createRange();
    beforeRange.selectNodeContents(node);
    beforeRange.setEndBefore(number);
    const beforeContent = beforeRange.extractContents();

    const text = document.createElement("span");
    text.className = "rv-rule-text";
    text.append(beforeContent, afterContent);

    node.replaceChildren(number, text);
    node.classList.add("rv-rule--hanging");
}

function decorateNumber(node) {
    if (!isSimpleBlock(node) || hasInternalLink(node)) return;
    const full = textOf(node);
    const match = full.match(NUMERIC_SUBPOINT);
    if (!match) return;
    const label = match[1];
    const depth = Math.min(label.split(".").length, 6);
    if (node.querySelector(".rv-num")) return;

    // Statt innerHTML-Regex nur das erste passende Text-Node bearbeiten.
    // Dadurch bleiben Hyperlinks, Hervorhebungen und Google-Docs-Inhalte erhalten.
    const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
    let textNode = walker.nextNode();
    while (textNode && !textNode.nodeValue.trim()) textNode = walker.nextNode();
    if (!textNode) return;
    const prefix = textNode.nodeValue.match(/^(\s*)((?:\d+\.)+\d+)\.?(?=\s|$)/);
    if (!prefix || prefix[2] !== label) return;

    const span = document.createElement("span");
    span.className = "rv-num";
    span.textContent = label;
    const frag = document.createDocumentFragment();
    if (prefix[1]) frag.appendChild(document.createTextNode(prefix[1]));
    frag.appendChild(span);
    frag.appendChild(document.createTextNode(textNode.nodeValue.slice(prefix[0].length)));
    textNode.parentNode.replaceChild(frag, textNode);
    node.classList.add("rv-rule", `rv-depth-${depth}`);
    node.dataset.rvRuleLabel = label;
    normalizeRuleLineLayout(node);
}

function decoratePills(root) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes = [];
    let current;
    while ((current = walker.nextNode())) {
        if (current.nodeValue.includes("[[pill:")) nodes.push(current);
    }
    for (const node of nodes) {
        const source = node.nodeValue;
        const frag = document.createDocumentFragment();
        let cursor = 0;
        DOC_PILL.lastIndex = 0;
        for (const found of source.matchAll(DOC_PILL)) {
            frag.appendChild(document.createTextNode(source.slice(cursor, found.index)));
            const pill = document.createElement("span");
            pill.className = `rv-doc-pill rv-doc-pill--${found[1].toLowerCase()}`;
            pill.textContent = found[2].trim();
            frag.appendChild(pill);
            cursor = found.index + found[0].length;
        }
        if (!cursor) continue;
        frag.appendChild(document.createTextNode(source.slice(cursor)));
        node.parentNode.replaceChild(frag, node);
    }
}

function decorateTables(root) {
    root.querySelectorAll("table").forEach((table) => {
        table.querySelectorAll("tr").forEach((tr) => {
            const first = tr.querySelector("td, th");
            const match = textOf(first).match(DOC_ROW);
            if (!match) return;
            const tone = match[1].toLowerCase() === "penalty" ? "red" : match[1].toLowerCase();
            tr.classList.add(`rv-table-row--${tone}`);
            const walker = document.createTreeWalker(first, NodeFilter.SHOW_TEXT);
            let textNode;
            while ((textNode = walker.nextNode())) {
                if (DOC_ROW.test(textNode.nodeValue)) {
                    textNode.nodeValue = textNode.nodeValue.replace(DOC_ROW, "");
                    break;
                }
            }
        });
        const scroller = document.createElement("div");
        scroller.className = "rv-rules-table-scroll";
        table.parentNode.insertBefore(scroller, table);
        scroller.appendChild(table);
    });
}

function groupSubsections(root) {
    let currentSection = null;
    for (const node of Array.from(root.children)) {
        if (node.classList.contains("rv-depth-2") && node.classList.contains("rv-rule")) {
            const section = document.createElement("section");
            section.className = "rv-rules-subsection";
            node.parentNode.insertBefore(section, node);
            section.appendChild(node);
            currentSection = section;
        } else if (currentSection) {
            currentSection.appendChild(node);
        }
    }
}

/**
 * Unnummerierte Fortsetzungsabsätze direkt nach einem Regelpunkt ebenfalls
 * an der Textspalte ausrichten. Tabellen/Listen und Ueberschriften bleiben
 * unveraendert.
 */
function markRuleContinuations(root) {
    const containers = [root, ...root.querySelectorAll(".rv-rules-subsection")];

    containers.forEach((container) => {
        let hasPrecedingRule = false;

        Array.from(container.children).forEach((node) => {
            if (node.classList.contains("rv-rule") && !node.matches("td, th")) {
                hasPrecedingRule = true;
                return;
            }

            if (
                hasPrecedingRule &&
                node.matches("p, div, blockquote") &&
                isSimpleBlock(node) &&
                textOf(node)
            ) {
                node.classList.add("rv-rule-continuation");
                return;
            }

            // Kein Einzug fuer Tabellen, Bilder, Ueberschriften oder Listen.
            hasPrecedingRule = false;
        });
    });
}

function decorateChapter(root) {
    root.querySelectorAll("p, div, li, td, th, h2, h3, h4").forEach(decorateNumber);
    root.querySelectorAll("a[href]").forEach((link) => {
        if (/^https?:\/\//i.test(link.getAttribute("href") || "")) {
            link.setAttribute("target", "_blank");
            link.setAttribute("rel", "noopener noreferrer");
        }
    });
    decorateTables(root);
    decoratePills(root);
    groupSubsections(root);
    markRuleContinuations(root);
}

function makeNumberCrosslink(element, target, direction) {
    const num = element.querySelector(".rv-num");
    if (!num) return;
    const link = document.createElement("a");
    link.href = `#${target}`;
    link.className = `rv-num rv-rule-crosslink rv-rule-crosslink--${direction}`;
    link.textContent = num.textContent;
    link.title = direction === "to-penalty" ? "Zum Strafenkatalog" : "Zur Regel";
    num.replaceWith(link);
}

function buildCrosslinks(chapters) {
    // Vorhandenes Feature beibehalten: Regelnummern ↔ gleicher Punkt im Strafenkatalog.
    const regular = new Map();
    const penalties = new Map();
    const anchorToChapter = {};
    chapters.forEach(({ no, root }) => {
        if (!root) return;
        let inPenaltyCatalog = false;
        root.querySelectorAll("h1, h2, h3, h4, p, div, li, td, th").forEach((node) => {
            if (/^(?:\d+\.?\s+)?(?:anlage\s+)?strafenkatalog\s*:?$/i.test(textOf(node))) {
                inPenaltyCatalog = true;
            }
            const label = node.dataset.rvRuleLabel;
            if (!label) return;
            const targetMap = inPenaltyCatalog ? penalties : regular;
            if (!targetMap.has(label)) targetMap.set(label, { element: node, chapterNo: no });
        });
    });
    regular.forEach((entry, label) => {
        const other = penalties.get(label);
        if (!other) return;
        const suffix = label.replace(/\./g, "-");
        const ruleId = `rule-${suffix}`;
        const penaltyId = `penalty-${suffix}`;
        entry.element.id = ruleId;
        other.element.id = penaltyId;
        anchorToChapter[ruleId] = entry.chapterNo;
        anchorToChapter[penaltyId] = other.chapterNo;
        makeNumberCrosslink(entry.element, penaltyId, "to-penalty");
        makeNumberCrosslink(other.element, ruleId, "to-rule");
    });
    return anchorToChapter;
}

function parseRulebookHtml(sanitizedHtml) {
    if (!sanitizedHtml || typeof document === "undefined") return null;
    const host = document.createElement("div");
    host.innerHTML = sanitizedHtml;
    unwrapGoogleBody(host);

    const roots = Object.fromEntries(CHAPTERS.map(({ no }) => [no, document.createElement("div")]));
    const chapterFound = new Set();
    const chapterHeading = {};
    const versions = document.createElement("div");
    let currentChapter = null;
    let readingVersions = false;
    let versionSeen = false;

    for (const node of Array.from(host.children)) {
        const line = textOf(node);
        const versionStart = VERSION_HEADING.test(line) && !hasInternalLink(node);
        if (line === "[[versions]]" || versionStart) {
            readingVersions = true;
            versionSeen = true;
            currentChapter = null;
            continue;
        }
        if (/^\[\[versions:end\]\]$/i.test(line) || /^inhaltsverzeichnis\s*:?$/i.test(line)) {
            readingVersions = false;
            continue;
        }
        const chapterNo = chapterNumberFromNode(node);
        if (chapterNo !== null) {
            currentChapter = chapterNo;
            readingVersions = false;
            chapterFound.add(chapterNo);
            if (!chapterHeading[chapterNo] && !stripChapterMarker(node)) chapterHeading[chapterNo] = line;
            // Kapitelüberschriften rendert die App selbst – nicht doppelt aus Google Docs.
            continue;
        }
        if (readingVersions) {
            versions.appendChild(node);
        } else if (currentChapter !== null) {
            roots[currentChapter].appendChild(node);
        }
        // Titelseite, Google-Docs-Inhaltsverzeichnis und Deko vor Kapitel 1 ausblenden.
    }

    const chapterRoots = CHAPTERS.map(({ no }) => ({ no, root: roots[no] }));
    chapterRoots.forEach(({ root }) => decorateChapter(root));
    decorateTables(versions);
    decoratePills(versions);
    const crosslinks = buildCrosslinks(chapterRoots);
    const searchIndex = indexRulebookChapters(chapterRoots);

    const chapterHtml = {};
    chapterRoots.forEach(({ no, root }) => { chapterHtml[no] = root.innerHTML; });
    return {
        chapters: chapterHtml,
        chapterFound: Array.from(chapterFound),
        chapterHeading,
        changesHtml: versions.innerHTML,
        versionSeen,
        crosslinks,
        searchIndex,
    };
}


/**
 * Suche ohne Beachtung von Gross-/Kleinschreibung und Akzenten.
 * Die Zuordnung zur Originalposition ermoeglicht exakte Hervorhebungen,
 * auch bei Umlauten und ss/ß.
 */
function foldSearchTextWithPositions(text) {
    let folded = "";
    const positions = [];
    for (let i = 0; i < text.length; i += 1) {
        const part = text[i].toLowerCase().normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/ß/g, "ss");
        folded += part;
        for (let k = 0; k < part.length; k += 1) positions.push(i);
    }
    return { folded, positions };
}

function searchTerms(query) {
    return String(query || "").trim().split(/\s+/)
        .map((term) => foldSearchTextWithPositions(term).folded)
        .filter(Boolean);
}

function findSearchRanges(text, query) {
    const terms = searchTerms(query);
    if (!terms.length) return [];
    const { folded, positions } = foldSearchTextWithPositions(text);
    const ranges = [];
    terms.forEach((term) => {
        let at = 0;
        while ((at = folded.indexOf(term, at)) !== -1) {
            ranges.push([positions[at], positions[at + term.length - 1] + 1]);
            at += Math.max(1, term.length);
        }
    });
    ranges.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
    const merged = [];
    ranges.forEach(([start, end]) => {
        const prev = merged[merged.length - 1];
        if (prev && start <= prev[1]) prev[1] = Math.max(prev[1], end);
        else merged.push([start, end]);
    });
    return merged;
}

function matchesSearch(text, terms) {
    const haystack = foldSearchTextWithPositions(text).folded;
    return terms.every((term) => haystack.includes(term));
}

function searchExcerpt(text, query) {
    const ranges = findSearchRanges(text, query);
    const first = ranges[0]?.[0] ?? 0;
    const start = Math.max(0, first - 65);
    const end = Math.min(text.length, Math.max(first + 115, start + 185));
    return `${start ? "…" : ""}${text.slice(start, end).trim()}${end < text.length ? "…" : ""}`;
}

function markedExcerpt(text, query) {
    const excerpt = searchExcerpt(text, query);
    const ranges = findSearchRanges(excerpt, query);
    if (!ranges.length) return excerpt;
    const result = [];
    let last = 0;
    ranges.forEach(([start, end], i) => {
        if (start > last) result.push(excerpt.slice(last, start));
        result.push(<mark key={`${start}-${end}-${i}`} className="rv-search-mark">{excerpt.slice(start, end)}</mark>);
        last = end;
    });
    if (last < excerpt.length) result.push(excerpt.slice(last));
    return result;
}

/**
 * Jedes durchsuchbare Textsegment erhaelt ein stabiles Ziel fuer den Sprung
 * aus einer Trefferliste. Durchsuche nicht gleichzeitig Eltern und Kinder.
 */
function indexRulebookChapters(chapterRoots) {
    const items = [];
    chapterRoots.forEach(({ no, root }) => {
        const chapter = CHAPTERS.find((entry) => entry.no === no);
        items.push({
            id: `rv-search-chapter-${no}`, chapterNo: no, chapterTitle: chapter?.title || "",
            text: `${chapter?.title || ""} ${chapter?.description || ""}`.trim(),
            label: "Kapitelübersicht", chapterOnly: true,
        });
        let previousRule = "";
        let counter = 0;
        root.querySelectorAll("p, li, h2, h3, h4, blockquote, td, th, div").forEach((node) => {
            if (node.querySelector("p, li, h2, h3, h4, blockquote, td, th, div")) return;
            const content = textOf(node);
            if (!content) return;
            const rule = node.dataset.rvRuleLabel || node.closest(".rv-rule")?.dataset.rvRuleLabel;
            if (rule) previousRule = rule;
            counter += 1;
            const id = `rv-search-${no}-${counter}`;
            node.setAttribute("data-rv-search-id", id);
            items.push({
                id, chapterNo: no, chapterTitle: chapter?.title || "",
                text: content, label: rule || previousRule || `Kapitel ${no}`,
                chapterOnly: false,
            });
        });
    });
    return items;
}

/** Nur im aktuell angezeigten Kapitel markieren, nie im gespeicherten HTML. */
function highlightRulebookHtml(html, query) {
    if (!html || !searchTerms(query).length || typeof document === "undefined") return html;
    const root = document.createElement("div");
    root.innerHTML = html;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes = [];
    let node;
    while ((node = walker.nextNode())) nodes.push(node);
    nodes.forEach((textNode) => {
        const value = textNode.nodeValue || "";
        const ranges = findSearchRanges(value, query);
        if (!ranges.length) return;
        const fragment = document.createDocumentFragment();
        let cursor = 0;
        ranges.forEach(([start, end]) => {
            if (start > cursor) fragment.appendChild(document.createTextNode(value.slice(cursor, start)));
            const mark = document.createElement("mark");
            mark.className = "rv-search-mark";
            mark.textContent = value.slice(start, end);
            fragment.appendChild(mark);
            cursor = end;
        });
        if (cursor < value.length) fragment.appendChild(document.createTextNode(value.slice(cursor)));
        textNode.parentNode.replaceChild(fragment, textNode);
    });
    return root.innerHTML;
}

function initialChapter() {
    if (typeof window === "undefined") return null;
    const match = window.location.hash.match(/^#chapter-(\d{1,2})$/);
    const no = match ? Number(match[1]) : null;
    return no && no >= 1 && no <= 15 ? no : null;
}

export default function Rules() {
    const [rulebook, setRulebook] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [selected, setSelected] = useState(initialChapter);
    const pendingAnchor = useRef("");
    const [searchQuery, setSearchQuery] = useState("");
    const [searchJumpSerial, setSearchJumpSerial] = useState(0);
    const searchInputRef = useRef(null);
    const pendingSearchTarget = useRef(null);


    useEffect(() => {
        let active = true;
        fetchPublicRulebook("gt3x")
            .then((data) => { if (active) setRulebook(data?.rulebook || null); })
            .catch((err) => { if (active) setError(err?.message || String(err)); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, []);

    // Auch direkte Kapitel-Links (#chapter-7) oder Browser-Zurück berücksichtigen.
    useEffect(() => {
        const onHashChange = () => setSelected(initialChapter());
        window.addEventListener("hashchange", onHashChange);
        return () => window.removeEventListener("hashchange", onHashChange);
    }, []);


    // Strg+F bzw. Cmd+F oeffnet die Suche ueber ALLE Kapitel.
    useEffect(() => {
        function handleRulebookSearchShortcut(event) {
            if ((event.ctrlKey || event.metaKey) && !event.altKey && event.key.toLowerCase() === "f") {
                event.preventDefault();
                searchInputRef.current?.focus();
                searchInputRef.current?.select();
            } else if (event.key === "Escape" && document.activeElement === searchInputRef.current) {
                setSearchQuery("");
                searchInputRef.current?.blur();
            }
        }
        window.addEventListener("keydown", handleRulebookSearchShortcut);
        return () => window.removeEventListener("keydown", handleRulebookSearchShortcut);
    }, []);

    const parsed = useMemo(() => {
        const raw = String(rulebook?.content_html || "");
        if (!raw) return null;
        const safe = DOMPurify.sanitize(raw, {
            USE_PROFILES: { html: true },
            ADD_ATTR: ["id", "class", "colspan", "rowspan", "target", "rel"],
        });
        return parseRulebookHtml(safe);
    }, [rulebook?.content_html]);

    useEffect(() => {
        if (!pendingAnchor.current) return;
        const id = pendingAnchor.current;
        const frame = window.requestAnimationFrame(() => {
            const target = document.getElementById(id);
            if (target) {
                target.scrollIntoView({ behavior: "smooth", block: "center" });
                target.classList.add("rv-rules-flash");
                window.setTimeout(() => target.classList.remove("rv-rules-flash"), 1700);
            }
            pendingAnchor.current = "";
        });
        return () => window.cancelAnimationFrame(frame);
    }, [selected, parsed]);

    function selectChapter(no) {
        setSelected(no);
        window.history.replaceState(null, "", `#chapter-${no}`);
        window.requestAnimationFrame(() => {
            document.getElementById("rv-rules-panel")?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
    }

    function backToOverview() {
        setSelected(null);
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
        scrollToTop();
    }

    function scrollToTop() {
        const main = document.querySelector(".main");
        if (main) main.scrollTo({ top: 0, behavior: "smooth" });
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    function documentClick(event) {
        const link = event.target.closest('a[href^="#"]');
        if (!link || !parsed) return;
        const id = link.getAttribute("href").slice(1);
        const otherChapter = parsed.crosslinks[id];
        if (!otherChapter) return;
        event.preventDefault();
        pendingAnchor.current = id;
        if (selected !== otherChapter) {
            setSelected(otherChapter);
        } else {
            const target = document.getElementById(id);
            target?.scrollIntoView({ behavior: "smooth", block: "center" });
            target?.classList.add("rv-rules-flash");
            window.setTimeout(() => target?.classList.remove("rv-rules-flash"), 1700);
            pendingAnchor.current = "";
        }
        window.history.replaceState(null, "", `#chapter-${otherChapter}`);
    }


    const terms = useMemo(() => searchTerms(searchQuery), [searchQuery]);
    const searchResults = useMemo(() => {
        if (!parsed || !terms.length) return [];
        return (parsed.searchIndex || []).filter((item) => matchesSearch(item.text, terms));
    }, [parsed, terms]);

    function jumpToSearchResult(hit) {
        pendingSearchTarget.current = { chapterNo: hit.chapterNo, id: hit.chapterOnly ? null : hit.id };
        window.history.replaceState(null, "", `#chapter-${hit.chapterNo}`);
        setSelected(hit.chapterNo);
        setSearchJumpSerial((value) => value + 1);
    }

    // Nach dem Kapitelwechsel zu genau dem gefundenen Absatz scrollen.
    useEffect(() => {
        const pending = pendingSearchTarget.current;
        if (!pending || pending.chapterNo !== selected) return;
        const frame = window.requestAnimationFrame(() => {
            const documentRoot = document.querySelector("#rv-rules-panel .rv-rules-document");
            const target = pending.id
                ? documentRoot?.querySelector(`[data-rv-search-id="${pending.id}"]`)
                : document.getElementById("rv-rules-panel");
            const destination = target || document.getElementById("rv-rules-panel");
            destination?.scrollIntoView({ behavior: "smooth", block: "center" });
            if (target) {
                target.classList.add("rv-rules-search-focus");
                window.setTimeout(() => target.classList.remove("rv-rules-search-focus"), 1900);
            }
            pendingSearchTarget.current = null;
        });
        return () => window.cancelAnimationFrame(frame);
    }, [selected, searchJumpSerial]);

    const current = CHAPTERS.find((c) => c.no === selected);
    const available = parsed?.chapterFound || [];
    const currentHtml = selected ? parsed?.chapters[selected] || "" : "";
    const displayedHtml = useMemo(
        () => highlightRulebookHtml(currentHtml, searchQuery),
        [currentHtml, searchQuery]
    );

    return (
        <div className="rv-page rv-rulebook-v2">
            <PageHero kicker="GT3 Xperience" title="Regelwerk">
                Das offizielle Reglement – nach Themen geordnet. Wähle eine Rubrik und lies direkt die Regeln aus der synchronisierten RaceVision-Version.
            </PageHero>

            {loading && <section className="rv-modern-card"><p>Regelwerk wird geladen …</p></section>}
            {!loading && error && <section className="rv-modern-card rv-rules-error"><h2>Regelwerk nicht verfügbar</h2><p>{error}</p></section>}
            {!loading && !error && !parsed && <section className="rv-modern-card rv-rules-error"><h2>Regelwerk noch nicht synchronisiert</h2><p>Bitte den Google-Docs-Sync im Adminbereich starten.</p></section>}

            {parsed && (
                <>
                    <section className="rv-rules-navigation" aria-label="Regelwerk Themenrubriken">
                        <div className="rv-rules-navigation-heading">
                            <div>
                                <div className="rv-section-kicker">Themengebiete</div>
                                <h2>Themen auswählen</h2>
                            </div>
                            {selected !== null && <button type="button" className="rv-rules-overview-button" onClick={backToOverview}>Zur Übersicht</button>}
                        </div>
                        <div className="rv-rules-search" role="search" aria-label="Gesamtes Regelwerk durchsuchen">
                            <div className="rv-rules-search-field">
                                <span className="rv-rules-search-icon" aria-hidden="true">⌕</span>
                                <input
                                    ref={searchInputRef}
                                    id="rv-rulebook-search"
                                    type="search"
                                    maxLength={120}
                                    autoComplete="off"
                                    placeholder="Alle 15 Kapitel durchsuchen, z. B. Überholen oder 9.2.2"
                                    aria-label="Suchbegriff im gesamten Regelwerk"
                                    value={searchQuery}
                                    onChange={(event) => setSearchQuery(event.target.value)}
                                />
                                {searchQuery && (
                                    <button className="rv-rules-search-clear" type="button" onClick={() => { setSearchQuery(""); searchInputRef.current?.focus(); }} aria-label="Suche löschen">✕</button>
                                )}
                                <kbd>Strg + F</kbd>
                            </div>
                            {terms.length > 0 && (
                                <div className="rv-rules-search-results" aria-live="polite">
                                    <div className="rv-rules-search-summary">
                                        <strong>{searchResults.length} {searchResults.length === 1 ? "Treffer" : "Treffer"}</strong>
                                        <span>im gesamten Regelwerk</span>
                                    </div>
                                    {searchResults.length === 0 ? (
                                        <p className="rv-rules-search-empty">Keine passenden Stellen gefunden. Versuche einen anderen Suchbegriff.</p>
                                    ) : (
                                        <div className="rv-rules-search-list">
                                            {searchResults.slice(0, 80).map((hit) => (
                                                <button
                                                    type="button"
                                                    key={hit.id}
                                                    className="rv-rules-search-result"
                                                    onClick={() => jumpToSearchResult(hit)}
                                                    aria-label={`Kapitel ${hit.chapterNo}, ${hit.label}: Suchtreffer öffnen`}
                                                >
                                                    <span className="rv-rules-search-result-meta">
                                                        <span className="rv-rules-search-chapter">{String(hit.chapterNo).padStart(2, "0")} · {hit.chapterTitle}</span>
                                                        <span className="rv-rules-search-label">{hit.label}</span>
                                                    </span>
                                                    <span className="rv-rules-search-excerpt">{markedExcerpt(hit.text, searchQuery)}</span>
                                                    <span className="rv-rules-search-arrow" aria-hidden="true">↗</span>
                                                </button>
                                            ))}
                                            {searchResults.length > 80 && (
                                                <div className="rv-rules-search-more">Es werden die ersten 80 Treffer angezeigt. Bitte die Suche eingrenzen.</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="rv-rules-category-grid">
                            {CHAPTERS.map((chapter) => (
                                <button
                                    type="button"
                                    key={chapter.no}
                                    className={`rv-rules-category rv-rules-category--${chapter.tone}${selected === chapter.no ? " is-active" : ""}`}
                                    onClick={() => selectChapter(chapter.no)}
                                    aria-pressed={selected === chapter.no}
                                    title={`Kapitel ${chapter.no}: ${chapter.title}`}
                                >
                                    <span className="rv-rules-category-top"><span className="rv-rules-category-icon" aria-hidden="true">{chapter.icon}</span><span className="rv-rules-category-number">{String(chapter.no).padStart(2, "0")}</span></span>
                                    <span className="rv-rules-category-title">{chapter.title}</span>
                                    <span className="rv-rules-category-bottom"><span>{chapter.description}</span><span className="rv-rules-category-arrow" aria-hidden="true">→</span></span>
                                </button>
                            ))}
                        </div>
                    </section>

                    {selected === null ? (
                        <section className="rv-modern-card rv-rules-versions" id="rv-rules-versions">
                            <div className="rv-rules-section-heading"><div><div className="rv-section-kicker">Dokumenthistorie</div><h2>Versionsänderungen</h2></div></div>
                            {parsed.changesHtml ? (
                                <div className="rv-rules-document rv-rules-changes" dangerouslySetInnerHTML={{ __html: parsed.changesHtml }} />
                            ) : (
                                <p className="rv-rules-hint">Noch keine Versionsänderungen erkannt. Im Google Doc den Abschnitt „Änderungsverfolgung“ oder den Marker <code>[[versions]]</code> verwenden.</p>
                            )}
                            <div className="rv-rules-sync-info">
                                <span>Letzter Sync: <strong>{prettyDate(rulebook?.last_synced_at)}</strong></span>
                                <span>Google geändert: <strong>{prettyDate(rulebook?.google_modified_time)}</strong></span>
                                <span>Drive-Revision: <strong>{rulebook?.google_version || "—"}</strong></span>
                            </div>
                        </section>
                    ) : (
                        <section className="rv-modern-card rv-rules-detail" id="rv-rules-panel">
                            <div className="rv-rules-detail-header"><div><div className="rv-section-kicker">Kapitel {String(selected).padStart(2, "0")} / 15</div><h2>{current?.title}</h2><p>{current?.description}</p></div><span className="rv-rules-detail-icon" aria-hidden="true">{current?.icon}</span></div>
                            {available.includes(selected) ? (
                                <div className="rv-rules-document" onClick={documentClick} dangerouslySetInnerHTML={{ __html: displayedHtml }} />
                            ) : (
                                <div className="rv-rules-hint">Kapitel {selected} wurde im Google-Docs-Export nicht erkannt. Prüfe, ob es im Dokument eine eigene Kapitelüberschrift „{selected}. …“ oder den Marker <code>[[chapter:{selected}]]</code> gibt.</div>
                            )}
                            <div className="rv-rules-detail-footer"><button type="button" className="rv-rules-overview-button" onClick={backToOverview}>Alle Themen</button><button type="button" className="rv-rules-next-button" onClick={() => selectChapter(selected === 15 ? 1 : selected + 1)}>Nächstes Kapitel →</button></div>
                        </section>
                    )}
                </>
            )}

            <button type="button" className="rv-rules-to-top" onClick={scrollToTop} aria-label="Zurück nach oben" title="Zurück nach oben"><span aria-hidden="true">↑</span><span>TOP</span></button>
        </div>
    );
}
